<template>
  <div class="typeform-page">
    <!-- Header -->
    <b-card no-body class="">
      <b-card-body class="py-2">
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div class="d-flex align-items-center">
            <div class="tf-icon mr-1">
              <i class="fas fa-poll-h"></i>
            </div>
            <div>
              <div class="tf-title">{{ msg('Typeform Leads') }}</div>
              <div class="tf-subtitle">{{ msg('Browse submissions by form') }}</div>
            </div>
          </div>

          <div class="d-flex align-items-center mt-1 mt-md-0 flex-wrap" style="gap:6px">
            <b-button
                v-if="selectedFormId !== null"
                variant="outline-success"
                class="tf-secondary-btn"
                @click="downloadExcelTemplate()"
            >
              <i class="fas fa-file-excel mr-50"></i> {{ msg('Excel Template') }}
            </b-button>

            <b-button
                v-if="selectedFormId !== null"
                variant="outline-warning"
                class="tf-secondary-btn"
                @click="dialogs.excelUpload = true"
            >
              <i class="fas fa-upload mr-50"></i> {{ msg('Import Excel') }}
            </b-button>

            <b-button
                v-if="selectedFormId !== null"
                variant="outline-primary"
                class="tf-secondary-btn"
                @click="openNewForm()"
            >
              <i class="fas fa-plus mr-50"></i> {{ msg('Fill new form') }}
            </b-button>

            <b-button variant="primary" class="tf-primary-btn" @click="refresh()">
              <i class="fas fa-sync-alt mr-50"></i> {{ msg('Reload') }}
            </b-button>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Tabs (formId) -->
    <b-card no-body class="">
      <b-card-body class="">
        <div class="d-flex align-items-center justify-content-between flex-wrap">
          <div class="tf-tabs-wrap">
            <b-nav pills class="tf-tabs">
              <b-nav-item
                  v-for="t in tabsWithAll"
                  :key="t.key"
                  :active="selectedFormId === t.id"
                  @click="selectForm(t.id)"
                  :class="{ 'tf-tab-divider': t.isAll }"
              >
                <span class="tf-tab-text">{{ t.label }}</span>
              </b-nav-item>
            </b-nav>
          </div>

          <div class="d-flex align-items-center mt-1 mt-md-0 flex-wrap">
            <div class="mr-1" style="min-width:260px">
              <label>{{ msg('Status') }}</label>
              <v-select
                  v-model="filter.in.status"
                  :options="statusOptions"
                  class="w-100"
                  label="text"
                  :reduce="(status) => status.value"
                  multiple
              />
            </div>

            <div class="mr-1" style="min-width:260px">
              <label>{{ msg('Source') }}</label>
              <v-select
                  v-model="filter.in.source"
                  :options="[
                  { value: 'CRM', text: msg('CRM') },
                  { value: 'typeform', text: msg('WEBSITE') },
                ]"
                  class="w-100"
                  label="text"
                  :reduce="(status) => status.value"
                  multiple
              />
            </div>

            <div class="tf-tool tf-search">
              <label class="tf-label mb-25">{{ msg('Search') }}</label>
              <b-input-group class="tf-search-group">
                <b-form-input
                    v-model="search"
                    :placeholder="msg('Name, email, token...')"
                    @keyup.enter="refresh()"
                />
                <b-input-group-append>
                  <b-button variant="outline-primary" @click="refresh()">
                    <i class="fas fa-search"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Content -->
    <b-card no-body class="tf-content-card">
      <b-card-body>
        <div v-if="loading" class="text-center my-4">
          <b-spinner class="align-middle mx-1"></b-spinner>
          <strong>{{ msg('Loading') }}...</strong>
        </div>

        <div v-else>
          <b-row>
            <b-col
                v-for="item in items"
                :key="item.id"
                cols="12"
                md="6"
                lg="4"
                class="mb-2"
            >
              <b-card class="tf-lead-card" @click="openDetails(item)">
                <div class="d-flex align-items-start justify-content-between">
                  <div class="tf-lead-main">
                    <div class="tf-lead-name">
                      {{ getDisplayName(item) }}
                    </div>
                    <div class="tf-lead-meta">
                      <div class="tf-modal-pill">
                        <i class="fas fa-network-wired "></i>
                        <span class="ml-50">{{ item.source === 'typeform' ? 'WEBSITE' : 'CRM' }}</span>
                      </div>

                      <span class="tf-pill">
                        <i class="far fa-clock mr-50"></i> {{ formatDate(item.submittedAt) }}
                      </span>

                      <span class="tf-pill tf-pill-status" :class="statusPillClass(item && item.status)">
                        <i class="fas fa-flag mr-50"></i>
                        {{ statusLabel(item && item.status) }}
                      </span>
                    </div>
                  </div>

                  <div class="tf-lead-actions">
                    <b-button
                        size="sm"
                        variant="link"
                        class="tf-del-btn"
                        @click.stop="askDelete(item)"
                        v-b-tooltip.hover
                        :title="msg('Delete')"
                    >
                      <i class="fas fa-trash"></i>
                    </b-button>

                    <div class="tf-lead-badge">
                      <i class="fas fa-chevron-right"></i>
                    </div>
                  </div>
                </div>

                <div class="tf-lead-details mt-1">
                  <div class="tf-kv" v-if="getEmail(item)">
                    <div class="tf-k">{{ msg('Email') }}</div>
                    <div class="tf-v">{{ getEmail(item) }}</div>
                  </div>

                  <div class="tf-kv" v-if="getPhone(item)">
                    <div class="tf-k">{{ msg('Phone') }}</div>
                    <div class="tf-v">{{ getPhone(item) }}</div>
                  </div>

                  <div class="tf-kv" v-if="getCompany(item)">
                    <div class="tf-k">{{ msg('Company') }}</div>
                    <div class="tf-v">{{ getCompany(item) }}</div>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <div v-if="items.length === 0" class="tf-empty">
            <div class="tf-empty-icon"><i class="far fa-folder-open"></i></div>
            <div class="tf-empty-title">{{ msg('No submissions found') }}</div>
            <div class="tf-empty-sub">{{ msg('Try a different form tab or search term') }}</div>
          </div>

          <!-- Pagination -->
          <div class="d-flex align-items-center justify-content-center mt-2 flex-wrap">
            <b-pagination
                v-model="page.currentPage"
                :total-rows="page.total"
                :per-page="page.amount"
                first-number
                last-number
                @input="refresh"
                class="tf-pagination"
            />
            <div class="ml-2 tf-total">
              <strong>{{ msg('Total') }}</strong> {{ page.total }}
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Details Modal -->
    <b-modal
        v-model="dialogs.details"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        body-class="tf-modal-body"
        content-class="tf-modal-content"
        dialog-class="tf-modal-dialog"
    >
      <template #modal-title>
        <div class="tf-modal-titlebar">
          <div class="d-flex align-items-center">
            <div class="tf-modal-icon mr-1"><i class="fas fa-clipboard-list"></i></div>
            <div>
              <div class="tf-modal-title">{{ getDisplayName(selectedItem) }}</div>
              <div class="tf-modal-sub">
                {{ (selectedItem && selectedItem.definition && selectedItem.definition.title) || msg('Form Submission') }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <transition name="tf-pop">
        <div v-if="selectedItem" class="tf-modal-wrap">
          <div class="tf-modal-top">
            <div class="tf-modal-pill">
              <i class="far fa-calendar-alt mr-50"></i>
              {{ msg('Submitted') }}:
              <span class="ml-50">{{ formatDate(selectedItem.submittedAt) }}</span>
            </div>
            <div class="tf-modal-pill">
              <i class="fas fa-network-wired mr-50"></i>
              {{ msg('Source') }}:
              <span class="ml-50">{{ selectedItem.source === 'typeform' ? 'WEBSITE' : 'CRM' }}</span>
            </div>

            <b-dropdown
                v-if="selectedItem"
                variant="link"
                no-caret
                toggle-class="p-0"
                menu-class="tf-status-menu"
                class="tf-status-dropdown"
            >
              <!-- The pill button (what you see) -->
              <template #button-content>
    <span class="tf-pill tf-pill-status" :class="statusPillClass(statusModel)">
      <i class="fas fa-flag mr-50"></i>
      {{ statusLabel(statusModel) }}
      <i class="fas fa-chevron-down ml-50"></i>
    </span>
              </template>

              <!-- Dropdown options -->
              <b-dropdown-item
                  v-for="opt in statusOptions"
                  :key="opt.value"
                  @click="onStatusChanged(opt.value)"
              >
    <span class="tf-pill tf-pill-status" :class="statusPillClass(opt.value)">
      <i class="fas fa-flag mr-50"></i>
      {{ opt.text }}
    </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <div class="tf-qa tf-qa-grid-2">
            <div
                v-for="qa in buildQAs(selectedItem)"
                :key="qa.key"
                class="tf-qa-row"
            >
              <div class="tf-q">
                <i class="far fa-question-circle mr-50"></i>
                {{ qa.question }}
              </div>
              <div class="tf-a">
                <span v-if="qa.answer && qa.answer !== ''">{{ qa.answer }}</span>
                <span v-else class="text-muted">-</span>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-2">
            <b-button
                variant="outline-success"
                class="mr-1"
                :disabled="!selectedItem"
                @click="openEmailPicker()"
            >
              <i class="fas fa-envelope mr-50"></i>
              {{ msg('Send email') }}
            </b-button>

            <b-button
                variant="outline-primary"
                class="mr-1"
                :disabled="!canConvertLead"
                @click="openConvertConfirm()"
            >
              <i class="fas fa-random mr-50"></i>
              {{ msg('Convert lead') }}
            </b-button>

            <b-button
                variant="outline-info"
                class="mr-1"
                :disabled="!selectedItem || statusModel !== 'DONE'"
                @click="openSendContract()"
            >
              <i class="fas fa-file-signature mr-50"></i>
              {{ msg('Send Contract') }}
            </b-button>

            <b-button variant="outline-secondary" @click="dialogs.details=false">
              {{ msg('Close') }}
            </b-button>
          </div>
        </div>
      </transition>
    </b-modal>


    <!-- Email Picker Modal -->
    <b-modal
        v-model="dialogs.emailPicker"
        centered
        :title="msg('Choose email event')"
        hide-footer
    >
      <div class="py-1">
        <div class="mb-1 text-muted">
          {{ msg('Select which email to send for this lead.') }}
        </div>

        <b-list-group>
          <b-list-group-item
              button
              @click="selectEmailEvent(emailEvents[0])"
          >
            <i class="fas fa-paper-plane mr-50"></i>
            {{ emailEvents[0].label }}
          </b-list-group-item>

          <b-list-group-item
              button
              @click="selectEmailEvent(emailEvents[1])"
          >
            <i class="fas fa-paper-plane mr-50"></i>
            {{ emailEvents[1].label }}
          </b-list-group-item>
        </b-list-group>
      </div>
    </b-modal>

    <!-- Email Confirm Modal -->
    <b-modal
        v-model="dialogs.emailConfirm"
        centered
        :title="msg('Confirm Action')"
        :ok-title="msg('Yes, send')"
        :cancel-title="msg('Cancel')"
        ok-variant="success"
        cancel-variant="secondary"
        @ok="confirmSendEmail"
    >
      <div class="py-1">
        {{ msg('Send') }}
        <strong>{{ selectedEmailEventLabel }}</strong>
        {{ msg('email to this lead?') }}
      </div>
    </b-modal>

    <!-- Convert Confirm -->
    <b-modal
        v-model="dialogs.convertConfirm"
        centered
        :title="msg('Confirm Action')"
        :ok-title="msg('Yes, convert')"
        :cancel-title="msg('Cancel')"
        ok-variant="primary"
        cancel-variant="secondary"
        @ok="confirmConvertLead"
    >
      <div class="py-1">
        {{ msg('Convert this lead to a branch and user?') }}
      </div>
    </b-modal>

    <!-- Send Contract Modal -->
    <b-modal
        v-model="dialogs.sendContract"
        centered
        :title="msg('Send Contract')"
        hide-footer
    >
      <div class="py-1">
        <b-form-group :label="msg('Select Branch')">
          <v-select
              v-model="contractBranchId"
              :options="allBranches"
              :reduce="b => b.id"
              label="name"
              :placeholder="msg('Choose a branch...')"
          />
        </b-form-group>
        <b-form-group :label="msg('Language')">
          <b-form-select v-model="contractLanguage" :options="[{value:'EN',text:'English'},{value:'DE',text:'Deutsch'}]"/>
        </b-form-group>
        <div class="d-flex justify-content-end mt-1" style="gap:8px">
          <b-button variant="secondary" size="sm" @click="dialogs.sendContract = false">{{ msg('Cancel') }}</b-button>
          <b-button variant="primary" size="sm" :disabled="!contractBranchId || creatingContract" @click="confirmSendContract">
            <b-spinner v-if="creatingContract" small class="mr-50"/>
            {{ msg('Create All Contracts') }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- New Form Modal -->
    <b-modal
        v-model="dialogs.newForm"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        body-class="tf-modal-body"
        content-class="tf-modal-content"
        dialog-class="tf-modal-dialog"
    >
      <template #modal-title>
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="d-flex align-items-center">
            <div class="tf-modal-icon mr-1"><i class="fas fa-edit"></i></div>
            <div>
              <div class="tf-modal-title">{{ msg('New submission') }}</div>
              <div class="tf-modal-sub">{{ (selectedFormTitle || selectedFormId) }}</div>
            </div>
          </div>
        </div>
      </template>

      <div class="d-flex align-items-start">
        <div>
          <label class="tf-label mb-25">{{ msg('Status') }}</label>
          <v-select
              v-model="newFormStatus"
              :options="statusOptions"
              label="text"
              :reduce="o => o.value"
              :clearable="false"
              class="tf-select tf-status-select"
          />
        </div>
      </div>

      <div v-if="newFormDefinition" class="tf-newform">
        <div
            v-for="f in newFormDefinition.fields"
            :key="fieldKey(f)"
            class="tf-newform-row"
        >
          <label class="tf-newform-label">
            {{ cleanTitle(f.title || fieldKey(f)) }}
          </label>

          <!-- short_text, long_text -->
          <b-form-input
              v-if="isTextField(f)"
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="cleanTitle(f.title || fieldKey(f))"
          />

          <!-- email -->
          <b-form-input
              v-else-if="f.type === 'email'"
              type="email"
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="msg('Email')"
          />

          <!-- phone -->
          <b-form-input
              v-else-if="f.type === 'phone_number'"
              type="tel"
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="msg('Phone')"
          />

          <!-- number -->
          <b-form-input
              v-else-if="f.type === 'number'"
              type="number"
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="msg('Number')"
          />

          <!-- yes_no -->
          <v-select
              v-else-if="f.type === 'yes_no'"
              v-model="newFormModel[fieldKey(f)]"
              :options="yesNoOptions"
              label="text"
              :reduce="o => o.value"
              :clearable="false"
              class="tf-select"
          />

          <!-- dropdown/multiple_choice => treat as TEXT (no dropdown UI) -->
          <b-form-input
              v-else-if="f.type === 'dropdown' || f.type === 'multiple_choice'"
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="cleanTitle(f.title || fieldKey(f))"
          />

          <!-- fallback -->
          <b-form-input
              v-else
              v-model="newFormModel[fieldKey(f)]"
              :placeholder="cleanTitle(f.title || fieldKey(f))"
          />
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button variant="outline-secondary" class="mr-1" @click="resetNewForm()">
            {{ msg('Reset') }}
          </b-button>
          <b-button variant="primary" @click="saveNewForm()">
            <i class="fas fa-save mr-50"></i> {{ msg('Save') }}
          </b-button>
        </div>
      </div>

      <div v-else class="py-2 text-muted">
        {{ msg('No form definition found for the selected tab.') }}
      </div>
    </b-modal>

    <!-- Delete Confirm -->
    <b-modal
        v-model="dialogs.confirmDelete"
        centered
        :title="msg('Confirm Action')"
        :ok-title="msg('Delete')"
        :cancel-title="msg('Cancel')"
        ok-variant="danger"
        cancel-variant="secondary"
        @ok="confirmDelete"
    >
      <div class="py-1">
        {{ msg('Are you sure you want to delete this submission?') }}
      </div>
    </b-modal>

    <!-- Excel Upload Modal -->
    <b-modal
        v-model="dialogs.excelUpload"
        size="lg"
        centered
        hide-footer
        no-close-on-backdrop
        body-class="tf-modal-body"
        content-class="tf-modal-content"
        dialog-class="tf-modal-dialog"
    >
      <template #modal-title>
        <div class="d-flex align-items-center">
          <div class="tf-modal-icon mr-1"><i class="fas fa-file-excel"></i></div>
          <div>
            <div class="tf-modal-title">{{ msg('Import Leads from Excel') }}</div>
            <div class="tf-modal-sub">{{ selectedFormTitle || selectedFormId }}</div>
          </div>
        </div>
      </template>

      <div class="tf-upload-wrap">
        <!-- Step 1: Upload -->
        <div v-if="!excelPreview.length">
          <div
              class="tf-dropzone"
              :class="{ 'tf-dropzone--over': dragOver }"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFileDrop"
              @click="$refs.excelFileInput.click()"
          >
            <input ref="excelFileInput" type="file" accept=".xlsx,.xls" class="d-none" @change="onFileSelect"/>
            <i class="fas fa-cloud-upload-alt tf-dropzone__icon"></i>
            <div class="tf-dropzone__title">{{ msg('Drop your Excel file here') }}</div>
            <div class="tf-dropzone__sub">{{ msg('or click to browse') }} &mdash; .xlsx only</div>
          </div>

          <div class="tf-upload-hint">
            <i class="fas fa-info-circle mr-50"></i>
            {{ msg('Use the "Excel Template" button to download a pre-formatted file with the correct columns.') }}
          </div>
        </div>

        <!-- Step 2: Preview -->
        <div v-else>
          <div class="tf-preview-header">
            <div>
              <strong>{{ excelPreview.length }}</strong> {{ msg('rows found') }}
            </div>
            <b-button size="sm" variant="outline-secondary" @click="clearExcelPreview">
              <i class="fas fa-times mr-50"></i> {{ msg('Clear') }}
            </b-button>
          </div>

          <div class="tf-preview-table-wrap">
            <table class="tf-preview-table">
              <thead>
                <tr>
                  <th v-for="col in excelColumns" :key="col">#{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, ri) in excelPreview.slice(0, 10)" :key="ri">
                  <td v-for="col in excelColumns" :key="col">{{ row[col] || '-' }}</td>
                </tr>
                <tr v-if="excelPreview.length > 10">
                  <td :colspan="excelColumns.length" class="text-center text-muted py-1">
                    ... {{ msg('and') }} {{ excelPreview.length - 10 }} {{ msg('more rows') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-end mt-2" style="gap:8px">
            <b-button variant="outline-secondary" @click="clearExcelPreview">
              {{ msg('Cancel') }}
            </b-button>
            <b-button variant="primary" :disabled="importing" @click="importExcelLeads">
              <b-spinner v-if="importing" small class="mr-50"/>
              <i v-else class="fas fa-upload mr-50"></i>
              {{ importing ? msg('Importing...') : msg('Import') + ' ' + excelPreview.length + ' ' + msg('leads') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import XLSX from 'xlsx-js-style'

export default {
  name: 'TypeformLeads',
  data() {
    return {
      masterFormTabs: [],
      masterFormTitles: {},
      masterFormDefinitions: {},


      emailEvents: [
        { key: 'leadMarketing', label: 'Marketing Template' },
        { key: 'leadOrderConfirmation', label: 'Bestellbestätigung ' }
      ],
      selectedEmailEventKey: null,
      selectedEmailEventLabel: '',


      loading: true,
      formTitles: {},
      formTabs: [],
      selectedFormId: null,

      items: [],
      search: '',
      filter: {
        like: { search: '' },
        eq: {},
        lte: {},
        gte: {},
        in: { status: [], source: [] }
      },
      sort: { by: 'creationDate', desc: true, direction: 'desc' },

      dialogs: {
        details: false,
        confirmDelete: false,
        newForm: false,
        emailPicker: false,
        emailConfirm: false,
        convertConfirm: false,
        excelUpload: false,
        sendContract: false,
      },

      dragOver: false,
      importing: false,
      excelPreview: [],
      excelColumns: [],

      newFormStatus: 'NEW',
      newFormModel: {},

      yesNoOptions: [
        { value: true, text: 'Yes' },
        { value: false, text: 'No' }
      ],

      page: { amount: 12, currentPage: 1, total: 0 },
      listeners: { refresh: () => {} },

      selectedItem: null,

      deleteCandidate: null,

      // convert
      convertCandidateId: null,
      converting: false,

      contractBranchId: null,
      contractLanguage: 'EN',
      creatingContract: false,

      // status
      statusModel: null,
      statusOptions: [
        { value: 'NEW', text: 'New' },
        { value: 'INPROGRESS', text: 'In Progress' },
        { value: 'DONE', text: 'Done' },
        { value: 'REJECTED', text: 'Rejected' }
      ]
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),

    allBranches() {
      return Object.values(this.$store.getters['data/getAllBranches'] || {})
    },

    canConvertLead() {
      if (!this.selectedItem || !this.selectedItem.definition || !this.selectedItem.definition.fields) return false
      const fields = this.selectedItem.definition.fields
      return fields.some(f => {
        if (!f || !f.title) return false
        const title = String(f.title).toLowerCase()
        return title.includes('first name') || title.includes('firstname') || title.includes('vorname')
      })
    },

    selectedFormTitle() {
      if (!this.selectedFormId) return ''
      return (this.formTitles && this.formTitles[this.selectedFormId]) ? this.formTitles[this.selectedFormId] : ''
    },

    // ✅ always prefer master definition cache
    newFormDefinition() {
      if (!this.selectedFormId) return null
      return this.selectedFormDefinition || null
    },

    selectedFormDefinition() {
      if (!this.selectedFormId) return null
      return this.masterFormDefinitions ? this.masterFormDefinitions[this.selectedFormId] : null
    },

    tabsWithAll() {
      const arr = []
      arr.push({ key: 'all', id: null, label: this.msg('All'), isAll: true })
      for (let i = 0; i < (this.formTabs || []).length; i++) {
        const fid = this.formTabs[i]
        arr.push({
          key: fid,
          id: fid,
          label: (this.formTitles && this.formTitles[fid]) ? this.formTitles[fid] : fid,
          isAll: false
        })
      }
      return arr
    }
  },

  watch: {
    filter: {
      deep: true,
      handler() {
        this.refresh()
      }
    }
  },

  created() {
    const $this = this
    this.refresh()
    this.$root.$on('crm::branch-updated', (this.listeners.refresh = () => $this.refresh()))
  },

  methods: {
    ...mapActions('typeform', ['getAll', 'deleteOne', 'updateTypeForm', 'createTypeForm', 'bulkCreate', 'convertLead', 'sendMailEvent']),
    openEmailPicker() {
      if (!this.selectedItem) return
      this.selectedEmailEventKey = null
      this.selectedEmailEventLabel = ''
      this.dialogs.emailPicker = true
    },

    selectEmailEvent(ev) {
      if (!ev) return
      this.selectedEmailEventKey = ev.key
      this.selectedEmailEventLabel = ev.label
      this.dialogs.emailPicker = false
      this.dialogs.emailConfirm = true
    },

    confirmSendEmail() {
      if (!this.selectedItem || !this.selectedEmailEventKey) return

      this.sendMailEvent({ id: this.selectedItem.id, mailType: this.selectedEmailEventKey })
          .then(() => {
            this.$bvToast && this.$bvToast.toast(this.msg('Email sent successfully.'), { variant: 'success' })
          })
          .catch(() => {
            this.$bvToast && this.$bvToast.toast(this.msg('Failed to send email.'), { variant: 'danger' })
          })
          .finally(() => {
            this.dialogs.emailConfirm = false
            this.selectedEmailEventKey = null
            this.selectedEmailEventLabel = ''
          })
    },
    // ✅ Mongo uses _id for fields. Always use this key in UI model.
    fieldKey(f) {
      return (f && (f._id || f.id)) || null
    },

    // convert dropdown/multiple_choice to normal text on CRM-created submissions
    normalizeFieldTypeForCrm(t) {
      const type = String(t || '').toLowerCase()
      if (type === 'dropdown' || type === 'multiple_choice') return 'short_text'
      return type
    },

    openConvertConfirm() {
      if (!this.selectedItem || !this.selectedItem.id) return
      this.convertCandidateId = this.selectedItem.id
      this.dialogs.convertConfirm = true
    },

    openSendContract() {
      this.contractBranchId = null
      this.contractLanguage = 'EN'
      this.dialogs.sendContract = true
    },

    confirmSendContract() {
      if (!this.contractBranchId) return
      this.creatingContract = true
      const leadId = this.selectedItem ? this.selectedItem.id : null
      this.$store.dispatch('contract/createAllContracts', {
        branchId: this.contractBranchId,
        leadId: leadId,
        language: this.contractLanguage,
      }).then(() => {
        this.dialogs.sendContract = false
        this.$router.push('/organization/contracts')
      }).catch(() => {
        this.$bvToast.toast(this.msg('Failed to create contracts'), { variant: 'danger' })
      }).finally(() => {
        this.creatingContract = false
      })
    },

    confirmConvertLead() {
      const id = this.convertCandidateId
      if (!id) return
      this.converting = true
      this.convertLead(id)
          .then(() => {
            this.dialogs.convertConfirm = false
            this.convertCandidateId = null
            this.dialogs.details = false
            this.refresh()
            window.open(`/organization/branches`, '_blank')
          })
          .catch(() => {
            this.$bvToast.toast(this.msg('Failed to convert lead. Please try again.'), { variant: 'danger' })
          })
          .finally(() => {
            this.converting = false
          })
    },

    openNewForm() {
      if (!this.selectedFormId) return
      if (!this.selectedFormDefinition || !this.selectedFormDefinition.fields) return

      this.resetNewForm()
      this.newFormStatus = 'NEW'
      this.dialogs.newForm = true
    },

    resetNewForm() {
      const def = this.selectedFormDefinition || this.newFormDefinition
      const model = {}
      if (def && def.fields) {
        def.fields.forEach(f => {
          const fid = this.fieldKey(f)
          if (fid) model[fid] = null
        })
      }
      this.newFormModel = model
    },

    isTextField(f) {
      const t = (f && f.type) ? String(f.type) : ''
      return t === 'short_text' || t === 'long_text' || t === 'text'
    },

    cleanTitle(title) {
      if (!title) return ''
      return String(title).replace(/_/g, '').trim()
    },

    saveNewForm() {
      if (!this.selectedFormId) return

      const def = this.selectedFormDefinition || this.newFormDefinition
      if (!def || !def.fields) return

      const nowIso = new Date().toISOString()

      const getFieldId = (f) => (f && (f._id || f.id)) // ✅ prefer _id
      const getFormId = (d) => (d && (d._id || d.id)) || this.selectedFormId

      // 1) Answers — dropdown => text
      const answers = (def.fields || [])
          .map(f => {
            const fid = getFieldId(f)
            if (!fid) return null

            const raw = this.newFormModel[fid]
            if (raw === null || raw === undefined || raw === '') return null

            const normalizedType = this.normalizeFieldTypeForCrm(f.type)

            const baseField = {
              _id: fid,
              id: fid,     // harmless
              ref: f.ref,
              type: normalizedType
            }

            if (normalizedType === 'email') {
              return { type: 'email', field: baseField, email: String(raw) }
            }

            if (normalizedType === 'phone_number') {
              const v = String(raw)
              // ✅ send BOTH naming styles so backend definitely maps it
              return {
                type: 'phone_number',
                field: baseField,
                phoneNumber: v,
                phone_number: v
              }
            }

            if (normalizedType === 'number') {
              return { type: 'number', field: baseField, numberValue: Number(raw) }
            }

            if (normalizedType === 'yes_no') {
              const v = !!raw
              // ✅ send multiple key names to survive any DTO naming
              return {
                type: 'boolean',
                field: baseField,
                booleanValue: v,
                boolean_value: v,
                boolean: v
              }
            }

            // ✅ everything else as text
            return { type: 'text', field: baseField, text: String(raw) }
          })
          .filter(Boolean)

      // 2) Definition — dropdown/multiple_choice converted to short_text; no choices
      const definitionToSend = {
        _id: getFormId(def),
        id: getFormId(def),
        title: def.title || this.selectedFormTitle || this.selectedFormId,
        fields: (def.fields || []).map(f => ({
          _id: getFieldId(f),
          id: getFieldId(f),
          ref: f.ref,
          type: this.normalizeFieldTypeForCrm(f.type), // ✅ dropdown -> short_text
          title: f.title,
          allowOtherChoice: false
        }))
      }

      const payload = {
        formId: this.selectedFormId,
        buid: this.buid, // keep if you have it, otherwise remove
        landedAt: nowIso,
        submittedAt: nowIso,
        source: 'CRM',
        status: this.newFormStatus,
        definition: definitionToSend,
        answers
      }

      this.createTypeForm({ form: payload })
          .then(() => {
            this.dialogs.newForm = false
            this.refresh()
          })
          .catch(() => {
            this.$bvToast.toast(this.msg('Failed to save. Please try again.'), { variant: 'danger' })
          })
    },

    selectForm(fid) {
      this.selectedFormId = fid
      this.filter.eq.formId = fid
      this.page.currentPage = 1
      this.refresh()
    },

    refresh() {
      const $this = this
      this.loading = true
      this.sort = { by: 'creationDate', desc: true, direction: 'desc' }

      this.getAll({
        page: $this.page.currentPage,
        amount: $this.page.amount,
        filter: $this.filter,
        sortcolumn: $this.sort.by,
        sortby: $this.sort.direction
      }).then(this.onRetrieve, (error) => (this.error = error))
    },

    onRetrieve(data) {
      this.page = {
        amount: data['Amount'] || this.page.amount,
        total: data['Total'] || 0,
        currentPage: data['Page'] || this.page.currentPage
      }

      this.items = data['Typeforms'] || []

      const titlesMap = this.extractFormTitlesFromItems(this.items)
      const idsList = this.extractFormIdsFromItems(this.items)

      if (this.selectedFormId === null) {
        this.masterFormTitles = titlesMap
        this.masterFormTabs = idsList
      }

      this.formTitles = this.masterFormTitles
      this.formTabs = this.masterFormTabs

      if (!this.formTabs || this.formTabs.length === 0) {
        this.formTitles = titlesMap
        this.formTabs = idsList
        this.masterFormTitles = titlesMap
        this.masterFormTabs = idsList
      }

      const defsMap = this.extractFormDefinitionsFromItems(this.items)
      if (this.selectedFormId === null) {
        this.masterFormDefinitions = { ...this.masterFormDefinitions, ...defsMap }
      }

      if (!this.masterFormDefinitions) this.masterFormDefinitions = {}
      this.loading = false
    },

    extractFormDefinitionsFromItems(items) {
      const map = {}
      ;(items || []).forEach(i => {
        const fid = i.formId || (i.definition && (i.definition.id || i.definition._id))
        if (!fid) return
        if (i.definition && !map[fid]) {
          map[fid] = i.definition
        }
      })
      return map
    },

    extractFormTitlesFromItems(items) {
      const map = {}
      ;(items || []).forEach(i => {
        const fid = i.formId || (i.definition && (i.definition.id || i.definition._id))
        if (!fid) return
        const title = (i.definition && i.definition.title) ? String(i.definition.title).trim() : ''
        if (title && !map[fid]) map[fid] = title
      })
      return map
    },

    extractFormIdsFromItems(items) {
      const set = {}
      ;(items || []).forEach(i => {
        const fid = i.formId || (i.definition && (i.definition.id || i.definition._id))
        if (fid) set[fid] = true
      })
      return Object.keys(set)
    },

    openDetails(item) {
      this.selectedItem = item
      this.statusModel = (item && item.status) ? item.status : 'NEW'
      this.dialogs.details = true
    },

    askDelete(item) {
      this.deleteCandidate = item
      this.dialogs.confirmDelete = true
    },

    confirmDelete() {
      if (!this.deleteCandidate || !this.deleteCandidate.id) return
      const id = this.deleteCandidate.id
      this.deleteCandidate = null

      this.deleteOne({ id })
          .then(() => {
            if (this.selectedItem && this.selectedItem.id === id) {
              this.dialogs.details = false
              this.selectedItem = null
            }
            this.refresh()
          })
          .catch(() => {})
    },

    onStatusChanged(newStatus) {
      this.statusModel = newStatus  // ✅ update pill text/color immediately
      if (!this.selectedItem || !this.selectedItem.id) return
      const id = this.selectedItem.id

      this.selectedItem.status = newStatus

      this.updateTypeForm({ form: this.selectedItem }).then(() => {
        const idx = this.items.findIndex(x => x && x.id === id)
        if (idx !== -1) this.items[idx].status = newStatus
      }).catch(() => {
        this.statusModel = this.selectedItem.status || 'NEW'
      })
    },

    statusLabel(status) {
      const s = status || 'NEW'
      const hit = this.statusOptions.find(x => x.value === s)
      return hit ? hit.text : s
    },

    statusPillClass(status) {
      const s = status || 'NEW'
      return {
        'is-new': s === 'NEW',
        'is-inProgress': s === 'INPROGRESS',
        'is-done': s === 'DONE',
        'is-rejected': s === 'REJECTED'
      }
    },

    // ✅ support both field._id and field.id in answers
    answerFieldId(ans) {
      return ans && ans.field ? (ans.field._id || ans.field.id) : null
    },

    getEmail(item) {
      const v = this.findAnswerByType(item, 'email') || this.findAnswerByTitle(item, ['email'])
      return v || ''
    },

    getPhone(item) {
      const v = this.findAnswerByType(item, 'phone_number') || this.findAnswerByTitle(item, ['phone'])
      return v || ''
    },

    getCompany(item) {
      return this.findAnswerByTitle(item, ['company', 'firma']) || ''
    },

    findAnswerByType(item, type) {
      if (!item || !item.answers) return ''
      const a = (item.answers || []).find(x => x && x.type === type)
      if (!a) return ''
      return (
          a.email ||
          a.phoneNumber ||
          a.phone_number ||
          a.text ||
          (a.choice && a.choice.label) ||
          (a.numberValue !== undefined ? String(a.numberValue) : '') ||
          (a.booleanValue === true ? 'Yes' : (a.booleanValue === false ? 'No' : '')) ||
          (a.boolean === true ? 'Yes' : (a.boolean === false ? 'No' : '')) ||
          ''
      )
    },

    findAnswerByTitle(item, keywords) {
      if (!item || !item.answers || !item.definition || !item.definition.fields) return ''
      const fields = item.definition.fields

      for (let i = 0; i < item.answers.length; i++) {
        const ans = item.answers[i]
        const fid = this.answerFieldId(ans)
        if (!fid) continue

        const def = fields.find(f => (f && (f._id || f.id)) === fid)
        const title = def && def.title ? String(def.title).toLowerCase() : ''
        if (!title) continue

        const hit = keywords.some(k => title.includes(String(k).toLowerCase()))
        if (!hit) continue

        return (
            ans.email ||
            ans.phoneNumber ||
            ans.phone_number ||
            ans.text ||
            (ans.choice && ans.choice.label) ||
            (ans.numberValue !== undefined ? String(ans.numberValue) : '') ||
            (ans.booleanValue === true ? 'Yes' : (ans.booleanValue === false ? 'No' : '')) ||
            (ans.boolean === true ? 'Yes' : (ans.boolean === false ? 'No' : '')) ||
            ''
        )
      }
      return ''
    },

    buildQAs(item) {
      if (!item || !item.definition || !item.definition.fields) return []

      const fields = item.definition.fields || []
      const answers = item.answers || []

      const answerByFieldId = {}
      answers.forEach(a => {
        const fid = this.answerFieldId(a)
        if (!fid) return

        const val =
            a.email ||
            a.phoneNumber ||
            a.phone_number ||
            a.text ||
            (a.choice && a.choice.label) ||
            (a.numberValue !== undefined ? String(a.numberValue) : '') ||
            (a.booleanValue === true ? 'Yes' : (a.booleanValue === false ? 'No' : '')) ||
            (a.boolean === true ? 'Yes' : (a.boolean === false ? 'No' : '')) ||
            ''

        answerByFieldId[fid] = val
      })

      return fields.map(f => {
        const fid = (f && (f._id || f.id)) || ''
        return {
          key: fid,
          question: f.title || fid,
          answer: answerByFieldId[fid] || ''
        }
      })
    },

    getDisplayName(item) {
      const first = this.findAnswerByTitle(item, ['first name', 'firstname', 'vorname'])
      const last = this.findAnswerByTitle(item, ['last name', 'lastname', 'nachname'])
      const email = this.getEmail(item)
      const full = [first, last].filter(Boolean).join(' ').trim()
      if (full) return full
      if (email) return email
      return this.msg('Lead')
    },

    // ---- Excel Template Download ----
    downloadExcelTemplate() {
      const def = this.selectedFormDefinition
      if (!def || !def.fields || !def.fields.length) return

      const headers = def.fields.map(f => this.cleanTitle(f.title || this.fieldKey(f)))
      const ws = XLSX.utils.aoa_to_sheet([headers])

      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 12 },
        fill: { fgColor: { rgb: '00B894' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          bottom: { style: 'thin', color: { rgb: '009975' } }
        }
      }

      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1')
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c })
        if (ws[addr]) {
          ws[addr].s = headerStyle
        }
      }

      ws['!cols'] = headers.map(h => ({ wch: Math.max(22, (h || '').length + 6) }))
      ws['!rows'] = [{ hpt: 28 }]

      const wb = XLSX.utils.book_new()
      const sheetName = (def.title || 'Leads').substring(0, 31)
      XLSX.utils.book_append_sheet(wb, ws, sheetName)
      XLSX.writeFile(wb, `${sheetName}_template.xlsx`)
    },

    // ---- Excel Upload ----
    onFileSelect(e) {
      const file = e.target.files && e.target.files[0]
      if (file) this.parseExcelFile(file)
      e.target.value = ''
    },

    onFileDrop(e) {
      this.dragOver = false
      const file = e.dataTransfer.files && e.dataTransfer.files[0]
      if (file) this.parseExcelFile(file)
    },

    isRowEmpty(row) {
      return Object.values(row).every(v => v === null || v === undefined || String(v).trim() === '')
    },

    parseExcelFile(file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: 'array' })
          const ws = wb.Sheets[wb.SheetNames[0]]
          const allRows = XLSX.utils.sheet_to_json(ws, { defval: '' })
          const rows = allRows.filter(r => !this.isRowEmpty(r))
          if (!rows.length) {
            this.$bvToast && this.$bvToast.toast(this.msg('The file appears to be empty or all rows are blank.'), { variant: 'warning' })
            return
          }
          const skipped = allRows.length - rows.length
          if (skipped > 0) {
            this.$bvToast && this.$bvToast.toast(`${skipped} empty row(s) were skipped.`, { variant: 'info' })
          }
          this.excelColumns = Object.keys(rows[0])
          this.excelPreview = rows
        } catch (err) {
          this.$bvToast && this.$bvToast.toast(this.msg('Failed to read Excel file.'), { variant: 'danger' })
        }
      }
      reader.readAsArrayBuffer(file)
    },

    clearExcelPreview() {
      this.excelPreview = []
      this.excelColumns = []
      this.importing = false
    },

    importExcelLeads() {
      const def = this.selectedFormDefinition
      if (!def || !def.fields) return
      if (!this.excelPreview.length) return

      this.importing = true
      const getFieldId = (f) => (f && (f._id || f.id))
      const getFormId = (d) => (d && (d._id || d.id)) || this.selectedFormId

      const definitionToSend = {
        _id: getFormId(def),
        id: getFormId(def),
        title: def.title || this.selectedFormTitle || this.selectedFormId,
        fields: (def.fields || []).map(f => ({
          _id: getFieldId(f),
          id: getFieldId(f),
          ref: f.ref,
          type: this.normalizeFieldTypeForCrm(f.type),
          title: f.title,
          allowOtherChoice: false
        }))
      }

      // Map clean titles back to field definitions
      const titleToField = {}
      ;(def.fields || []).forEach(f => {
        const clean = this.cleanTitle(f.title || this.fieldKey(f))
        titleToField[clean] = f
      })

      const nowIso = new Date().toISOString()
      const leads = this.excelPreview.map(row => {
        const answers = []
        for (const colName of this.excelColumns) {
          const raw = row[colName]
          if (raw === null || raw === undefined || raw === '') continue
          const field = titleToField[colName]
          if (!field) continue

          const fid = getFieldId(field)
          const normalizedType = this.normalizeFieldTypeForCrm(field.type)
          const baseField = { _id: fid, id: fid, ref: field.ref, type: normalizedType }

          if (normalizedType === 'email') {
            answers.push({ type: 'email', field: baseField, email: String(raw) })
          } else if (normalizedType === 'phone_number') {
            answers.push({ type: 'phone_number', field: baseField, phoneNumber: String(raw), phone_number: String(raw) })
          } else if (normalizedType === 'number') {
            answers.push({ type: 'number', field: baseField, numberValue: Number(raw) })
          } else if (normalizedType === 'yes_no' || normalizedType === 'boolean') {
            const v = String(raw).toLowerCase()
            const bool = v === 'yes' || v === 'true' || v === '1' || v === 'ja'
            answers.push({ type: 'boolean', field: baseField, booleanValue: bool, boolean: bool })
          } else {
            answers.push({ type: 'text', field: baseField, text: String(raw) })
          }
        }

        return {
          formId: this.selectedFormId,
          landedAt: nowIso,
          submittedAt: nowIso,
          source: 'CRM',
          status: 'NEW',
          definition: definitionToSend,
          answers
        }
      })

      this.bulkCreate({ leads })
          .then(() => {
            this.dialogs.excelUpload = false
            this.clearExcelPreview()
            this.refresh()
          })
          .catch(() => {
            this.$bvToast && this.$bvToast.toast(this.msg('Import failed. Please try again.'), { variant: 'danger' })
          })
          .finally(() => {
            this.importing = false
          })
    },

    formatDate(val) {
      if (!val) return '-'
      if (this.$moment) return this.$moment(val).format('DD.MM.YYYY HH:mm')
      return String(val)
    }
  }
}
</script>
<style scoped>
/* =========================================================
   Typeform Leads - Clean merged theme (no duplicates)
   More contrast + nicer tabs + better background separation
   ========================================================= */

.typeform-page {
  padding-bottom: 20px;
  /* subtle page background to separate cards from page */
  background: radial-gradient(1200px 400px at 20% 0%, rgba(0, 184, 148, .06), transparent 60%),
  radial-gradient(1000px 380px at 90% 10%, rgba(115, 103, 240, .06), transparent 55%);
  border-radius: 18px;
}

/* ---------- Cards general look ---------- */
.tf-header-card,
.tf-tabs-card,
.tf-content-card {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .06);
  box-shadow: 0 10px 26px rgba(0, 0, 0, .06);
  //background: rgba(249, 16, 16, 0.92);
  backdrop-filter: blur(6px);
}

.tf-header-card {
  margin-bottom: 14px;
}

.tf-tabs-card {
  margin-bottom: 14px;
}

.tf-content-card {
}

/* Header */
.tf-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 103, 240, .14);
  color: #7367f0;
}

.tf-title {
  font-weight: 900;
  font-size: 16px;
  line-height: 1.1;
  letter-spacing: .2px;
}

.tf-subtitle {
  font-size: 12px;
  opacity: .78;
}

.tf-refresh {
  opacity: .8;
}

.tf-refresh:hover {
  opacity: 1;
}

.tf-primary-btn {
  border-radius: 12px;
}

/* ---------- Tabs (make them pop & not blend) ---------- */
.tf-tabs-wrap {
  max-width: 100%;
  overflow: auto;
  padding-bottom: 2px;
}

.tf-tabs {
  flex-wrap: nowrap;
  padding: 2px;
  border-radius: 999px;
  background: rgba(0, 0, 0, .03);
  border: 1px solid rgba(0, 0, 0, .05);
}

.tf-tabs :deep(.nav-link) {
  border-radius: 999px !important;
  padding: 8px 14px;
  margin-right: 6px;
  font-weight: 800;
  font-size: 12px;
  background: rgba(255, 255, 255, .85);
  border: 1px solid rgba(0, 0, 0, .06);
  color: inherit;
  transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
  white-space: nowrap;
}

.tf-tabs :deep(.nav-link:hover) {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, .08);
  background: rgba(255, 255, 255, .95);
}

/* ACTIVE TAB: teal accent */
.tf-tabs :deep(.nav-link.active) {
  background: linear-gradient(180deg, rgba(0, 184, 148, .16), rgba(0, 184, 148, .10)) !important;
  border-color: rgba(0, 184, 148, .35) !important;
  color: #00b894 !important;
  box-shadow: 0 12px 22px rgba(0, 0, 0, .10);
}

.tf-tab-text {
  white-space: nowrap;
}

/* Tools */
.tf-tools {
  gap: 12px;
}

.tf-label {
  font-size: 11px;
  opacity: .75;
}

.tf-select :deep(.vs__dropdown-toggle) {
  border-radius: 12px;
}

.tf-search-group .form-control {
  border-radius: 12px 0 0 12px;
}

.tf-search-group .btn {
  border-radius: 0 12px 12px 0;
}

/* ---------- Lead cards: more contrast + stripe accent ---------- */
.tf-lead-card {
  border-radius: 18px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .08);
  background: rgba(209, 225, 235, 0.68);
  box-shadow: 0 10px 22px rgba(0, 0, 0, .08);
  transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease;
}

.dark-layout .tf-lead-card {
  border-radius: 18px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .08);
  background: rgba(61, 77, 118, 0.38);
  box-shadow: 0 10px 22px rgba(0, 0, 0, .08);
  transition: transform .16s ease, box-shadow .16s ease, border-color .16s ease;
}

/* left accent stripe */
.tf-lead-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 7px;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 184, 148, .95), rgba(0, 184, 148, .25));
}

/* subtle top highlight */
.tf-lead-card::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 1px;
  background: rgba(0, 0, 0, .04);
}

.tf-lead-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 16px 34px rgba(0, 0, 0, .14);
  border-color: rgba(0, 184, 148, .30);
}

.tf-lead-card :deep(.card-body) {
  padding-left: 16px; /* space from stripe */
}

.tf-lead-name {
  font-weight: 900;
  font-size: 15px;
  line-height: 1.2;
  letter-spacing: .1px;
}

.tf-lead-meta {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* pills: teal accent for contrast */
.tf-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(0, 184, 148, .10);
  border: 1px solid rgba(0, 184, 148, .18);
  color: rgba(0, 184, 148, .95);
}

.tf-lead-badge {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, .035);
  border: 1px solid rgba(0, 0, 0, .06);
}

/* Details blocks inside card (more readable) */
.tf-lead-details {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.tf-kv {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 8px;
  padding: 9px 10px;
  border-radius: 14px;
  background: rgba(0, 0, 0, .018);
  border: 1px solid rgba(0, 0, 0, .06);
}

.tf-k {
  font-size: 11px;
  opacity: .75;
}

.tf-v {
  font-size: 12px;
  font-weight: 800;
  word-break: break-word;
}

/* Empty state */
.tf-empty {
  text-align: center;
  padding: 26px 0;
}

.tf-empty-icon {
  font-size: 34px;
  opacity: .5;
  margin-bottom: 8px;
}

.tf-empty-title {
  font-weight: 900;
}

.tf-empty-sub {
  opacity: .75;
  font-size: 12px;
}

/* Pagination */
.tf-pagination :deep(.page-link) {
  border-radius: 10px !important;
}

.tf-total {
  font-size: 12px;
  opacity: .85;
}

/* ---------- Modal: more contrast + teal accent ---------- */
.tf-modal-dialog :deep(.modal-dialog) {
  border-radius: 18px;
}

.tf-modal-content {
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, .10);
  box-shadow: 0 18px 50px rgba(0, 0, 0, .18);
}

.tf-modal-body {
  padding: 18px;
  background: rgba(255, 255, 255, .98);
}

.tf-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 184, 148, .12);
  color: #00b894;
}

.tf-modal-title {
  font-weight: 900;
  font-size: 15px;
  line-height: 1.2;
}

.tf-modal-sub {
  font-size: 12px;
  opacity: .78;
}

.tf-modal-top {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.tf-modal-pill {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  padding: 9px 11px;
  border-radius: 999px;
  background: rgba(0, 184, 148, .10);
  border: 1px solid rgba(0, 184, 148, .18);
  color: rgba(0, 184, 148, .95);
}

.tf-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

/* Q/A blocks */
.tf-qa {
  display: grid;
  gap: 10px;
}

.tf-qa-row {
  border: 1px solid rgba(0, 0, 0, .08);
  border-radius: 16px;
  padding: 12px 12px;
  background: rgba(0, 0, 0, .012);
  position: relative;
  overflow: hidden;
}

.tf-qa-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 6px;
  height: 100%;
  background: linear-gradient(180deg, rgba(0, 184, 148, .95), rgba(0, 184, 148, .25));
}

.tf-q {
  font-weight: 900;
  font-size: 12px;
  margin-bottom: 6px;
  padding-left: 6px;
}

.tf-a {
  font-size: 13px;
  font-weight: 800;
  word-break: break-word;
  padding-left: 6px;
}

/* Animation */
.tf-pop-enter-active, .tf-pop-leave-active {
  transition: all .18s ease;
}

.tf-pop-enter, .tf-pop-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(.99);
}

@media (max-width: 768px) {
  .tf-kv {
    grid-template-columns: 76px 1fr;
  }
}

/* Card actions: delete + chevron */
.tf-lead-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tf-del-btn {
  padding: 0;
  width: 34px;
  height: 34px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ea5455; /* red */
  background: rgba(234, 84, 85, .10);
  border: 1px solid rgba(234, 84, 85, .22);
  transition: transform .12s ease, box-shadow .12s ease, background .12s ease;
}

.tf-del-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(234, 84, 85, .18);
  background: rgba(234, 84, 85, .14);
}

/* Modal title bar layout */
.tf-modal-titlebar {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.tf-modal-status {
  min-width: 210px;
  text-align: right;
}

.tf-status-select :deep(.vs__dropdown-toggle) {
  border-radius: 12px;
}

/* Status pill variants */
.tf-pill-status {
  border: 1px solid rgba(0, 0, 0, .06);
}

.tf-pill-status.is-new {
  background: rgba(255, 159, 67, .14);
  border-color: rgba(255, 159, 67, .28);
  color: rgba(255, 159, 67, 1);
}

.tf-pill-status.is-inProgress {
  background: rgba(0, 207, 232, .14);
  border-color: rgba(0, 207, 232, .28);
  color: rgba(0, 207, 232, 1);
}

.tf-pill-status.is-done {
  background: rgba(40, 199, 111, .14);
  border-color: rgba(40, 199, 111, .28);
  color: rgba(40, 199, 111, 1);
}

.tf-pill-status.is-rejected {
  background: rgba(234, 84, 85, .14);
  border-color: rgba(234, 84, 85, .28);
  color: rgba(234, 84, 85, 1);
}

@media (max-width: 768px) {
  .tf-modal-titlebar {
    flex-direction: column;
    align-items: stretch;
  }

  .tf-modal-status {
    text-align: left;
    min-width: unset;
  }
}

/* modal top row */
.tf-modal-top {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center; /* <-- important */
  margin-bottom: 12px;
}

/* inline select container */
.tf-status-inline {
  display: flex;
  align-items: center;
}

/* make v-select look like a pill button */
.tf-status-inline-select :deep(.vs__dropdown-toggle) {
  border-radius: 999px !important;
  min-height: 34px;
  padding: 0 10px;
  background: rgba(255, 255, 255, .85);
  border: 1px solid rgba(0, 0, 0, .08);
}

/* keep the inside text compact */
.tf-status-inline-select :deep(.vs__selected) {
  font-weight: 800;
  font-size: 12px;
}

/* keep caret aligned */
.tf-status-inline-select :deep(.vs__actions) {
  padding-right: 6px;
}

/* optional: when focusing, highlight teal */
.tf-status-inline-select :deep(.vs--open .vs__dropdown-toggle),
.tf-status-inline-select :deep(.vs__dropdown-toggle:focus-within) {
  border-color: rgba(0, 184, 148, .35) !important;
  box-shadow: 0 10px 22px rgba(0, 0, 0, .10);
}

/* keep all pills in one line */
.tf-tabs {
  display: flex;
  align-items: center;
}

/* separator between All and the rest */
.tf-tab-sep {
  display: inline-block;
  width: 1px;
  height: 24px;
  margin: 0 10px;
  background: rgba(0, 0, 0, .12);
  border-radius: 2px;
  flex: 0 0 auto;
}

/* on dark layout, make separator visible */
.dark-layout .tf-tab-sep {
  background: rgba(255, 255, 255, .18);
}

/* optional: slightly bolder All pill text */
.tf-tab-all :deep(.nav-link) {
  font-weight: 900;
}

/* vertical separator after "All" */
.tf-tabs :deep(li.nav-item.tf-tab-divider) {
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid rgba(0, 0, 0, .14);
}

.dark-layout .tf-tabs :deep(li.nav-item.tf-tab-divider) {
  border-right: 1px solid rgba(255, 255, 255, .18);
}

.tf-modal-status--new {
  min-width: 200px;
  text-align: right;
}

@media (max-width: 768px) {
  .tf-modal-status--new {
    text-align: left;
    min-width: unset;
  }
}
.tf-qa-grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr; /* ✅ two questions per row */
  gap: 10px;
}

@media (max-width: 768px) {
  .tf-qa-grid-2 {
    grid-template-columns: 1fr; /* stack on mobile */
  }
}

.tf-status-dropdown :deep(.dropdown-toggle) {
  text-decoration: none !important;
}

.tf-status-menu {
  border-radius: 14px;
  padding: 8px;
}

.tf-status-menu :deep(.dropdown-item) {
  border-radius: 12px;
  padding: 6px 8px;
}

/* ---------- Excel Upload ---------- */
.tf-secondary-btn {
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
}

.tf-upload-wrap {
  padding: 4px 0;
}

.tf-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed rgba(0, 184, 148, 0.3);
  border-radius: 16px;
  background: rgba(0, 184, 148, 0.04);
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.tf-dropzone:hover,
.tf-dropzone--over {
  border-color: rgba(0, 184, 148, 0.6);
  background: rgba(0, 184, 148, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(0, 184, 148, 0.1);
}

.tf-dropzone__icon {
  font-size: 36px;
  color: #00b894;
  margin-bottom: 12px;
  opacity: 0.7;
}

.tf-dropzone__title {
  font-weight: 800;
  font-size: 14px;
  margin-bottom: 4px;
}

.tf-dropzone__sub {
  font-size: 12px;
  opacity: 0.6;
}

.tf-upload-hint {
  margin-top: 14px;
  padding: 10px 14px;
  background: rgba(115, 103, 240, 0.06);
  border: 1px solid rgba(115, 103, 240, 0.12);
  border-radius: 10px;
  font-size: 12px;
  color: #7367f0;
  font-weight: 600;
}

.tf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  font-size: 14px;
}

.tf-preview-table-wrap {
  max-height: 320px;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
}

.tf-preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.tf-preview-table th {
  position: sticky;
  top: 0;
  background: rgba(0, 184, 148, 0.1);
  font-weight: 800;
  font-size: 11px;
  padding: 10px 12px;
  white-space: nowrap;
  border-bottom: 2px solid rgba(0, 184, 148, 0.2);
}

.tf-preview-table td {
  padding: 8px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tf-preview-table tbody tr:hover {
  background: rgba(0, 0, 0, 0.02);
}
</style>
